import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { 
  CheckCircle2, Circle, Sparkles, Clock, CreditCard, 
  Users, Calendar, FileText, Settings, ArrowRight,
  PlayCircle, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function WelcomeDashboard({ user, clinic }) {
  const [quickStartProgress, setQuickStartProgress] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [trialInfo, setTrialInfo] = useState(null);

  useEffect(() => {
    loadProgress();
    loadTrialInfo();
  }, []);

  const loadProgress = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/onboarding/progress`);
      setCompletedSteps(response.data.completed_steps || []);
      setQuickStartProgress(response.data.progress || 0);
    } catch (error) {
      console.error('Error loading progress:', error);
    }
  };

  const loadTrialInfo = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/clinics/${clinic.id}/trial-status`);
      setTrialInfo(response.data);
    } catch (error) {
      console.error('Error loading trial info:', error);
    }
  };

  const quickStartSteps = [
    {
      id: 'branding',
      title: 'Customize Your Branding',
      description: 'Add your logo and colors',
      icon: Settings,
      action: () => window.location.href = '/admin/settings',
      estimatedTime: '2 min'
    },
    {
      id: 'membership_plan',
      title: 'Create Your First Membership Plan',
      description: 'Set up pricing and benefits',
      icon: FileText,
      action: () => window.location.href = '/admin/plans',
      estimatedTime: '5 min'
    },
    {
      id: 'add_member',
      title: 'Add Your First Member',
      description: 'Import or manually add members',
      icon: Users,
      action: () => window.location.href = '/admin/members',
      estimatedTime: '3 min'
    },
    {
      id: 'schedule_appointment',
      title: 'Schedule an Appointment',
      description: 'Try the booking system',
      icon: Calendar,
      action: () => window.location.href = '/admin/calendar',
      estimatedTime: '2 min'
    },
    {
      id: 'payment_gateway',
      title: 'Connect Payment Gateway',
      description: 'Choose Stripe, PayPal, or Square',
      icon: CreditCard,
      action: () => window.location.href = '/admin/settings/payments',
      estimatedTime: '5 min'
    }
  ];

  const handleStepComplete = async (stepId) => {
    try {
      await axios.post(`${API_URL}/api/onboarding/complete-step`, { step_id: stepId });
      setCompletedSteps([...completedSteps, stepId]);
      
      const newProgress = ((completedSteps.length + 1) / quickStartSteps.length) * 100;
      setQuickStartProgress(newProgress);
      
      toast.success('Step completed! 🎉');
    } catch (error) {
      console.error('Error marking step complete:', error);
    }
  };

  const getTrialDaysLeft = () => {
    if (!trialInfo || !trialInfo.trial_end_date) return null;
    const endDate = new Date(trialInfo.trial_end_date);
    const today = new Date();
    const daysLeft = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
    return daysLeft > 0 ? daysLeft : 0;
  };

  const daysLeft = getTrialDaysLeft();

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-teal-600 to-cyan-600 rounded-lg p-6 text-white">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">
              Welcome, {user?.full_name}! 🎉
            </h1>
            <p className="text-teal-100 text-lg">
              Let's get {clinic?.name} set up and ready to go
            </p>
          </div>
          {daysLeft !== null && (
            <div className="bg-white/20 backdrop-blur rounded-lg px-4 py-3 text-center">
              <div className="text-2xl font-bold">{daysLeft}</div>
              <div className="text-xs text-teal-100">Days left in trial</div>
            </div>
          )}
        </div>
      </div>

      {/* Trial Status Alert */}
      {daysLeft !== null && daysLeft <= 7 && (
        <Card className={`border-2 ${
          daysLeft <= 3 ? 'border-red-500 bg-red-50' : 
          daysLeft <= 7 ? 'border-amber-500 bg-amber-50' : 
          'border-blue-500 bg-blue-50'
        }`}>
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <AlertCircle className={`h-6 w-6 flex-shrink-0 ${
                daysLeft <= 3 ? 'text-red-600' : 'text-amber-600'
              }`} />
              <div className="flex-1">
                <h3 className="font-semibold text-lg mb-1">
                  {daysLeft <= 3 ? '⏰ Your trial ends soon!' : '📅 Trial ending soon'}
                </h3>
                <p className="text-gray-700 mb-3">
                  {daysLeft === 0 
                    ? 'Your trial ends today. Add a payment method to continue using My MedMember.'
                    : `Your trial ends in ${daysLeft} days. Add a payment method now to ensure uninterrupted access.`
                  }
                </p>
                <Button 
                  size="sm" 
                  className="bg-gradient-to-r from-teal-600 to-cyan-600"
                  onClick={() => window.location.href = '/admin/billing'}
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  Add Payment Method
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Start Checklist */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-teal-600" />
                Quick Start Checklist
              </CardTitle>
              <CardDescription>
                Complete these steps to get the most out of your platform
              </CardDescription>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-teal-600">
                {completedSteps.length}/{quickStartSteps.length}
              </div>
              <div className="text-xs text-gray-500">Completed</div>
            </div>
          </div>
          <Progress value={quickStartProgress} className="mt-4" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {quickStartSteps.map((step, index) => {
              const isCompleted = completedSteps.includes(step.id);
              const Icon = step.icon;
              
              return (
                <div
                  key={step.id}
                  className={`flex items-center gap-4 p-4 rounded-lg border-2 transition-all ${
                    isCompleted 
                      ? 'bg-green-50 border-green-200' 
                      : 'bg-white border-gray-200 hover:border-teal-300 hover:shadow-md'
                  }`}
                >
                  <div className="flex-shrink-0">
                    {isCompleted ? (
                      <CheckCircle2 className="h-6 w-6 text-green-600" />
                    ) : (
                      <Circle className="h-6 w-6 text-gray-400" />
                    )}
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className="h-4 w-4 text-gray-500" />
                      <h4 className={`font-semibold ${isCompleted ? 'text-green-900' : 'text-gray-900'}`}>
                        {step.title}
                      </h4>
                      <Badge variant="outline" className="text-xs">
                        <Clock className="h-3 w-3 mr-1" />
                        {step.estimatedTime}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600">{step.description}</p>
                  </div>
                  
                  {!isCompleted && (
                    <Button 
                      size="sm"
                      onClick={step.action}
                      className="flex-shrink-0"
                    >
                      Start
                      <ArrowRight className="h-4 w-4 ml-1" />
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Video Tutorials */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PlayCircle className="h-5 w-5 text-blue-600" />
            Video Tutorials
          </CardTitle>
          <CardDescription>Quick guides to help you get started</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { title: 'Platform Overview', duration: '3:45' },
              { title: 'Creating Membership Plans', duration: '5:20' },
              { title: 'Managing Members', duration: '4:15' }
            ].map((video, index) => (
              <div 
                key={index}
                className="bg-gray-100 rounded-lg p-4 cursor-pointer hover:bg-gray-200 transition-colors"
              >
                <div className="aspect-video bg-gray-300 rounded-md mb-3 flex items-center justify-center">
                  <PlayCircle className="h-12 w-12 text-white" />
                </div>
                <h4 className="font-semibold mb-1">{video.title}</h4>
                <p className="text-sm text-gray-600">{video.duration}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Need Help */}
      <Card className="bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="bg-purple-100 p-3 rounded-full">
              <svg className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-lg mb-1">Need Help Getting Started?</h3>
              <p className="text-gray-700 mb-3">
                Our support team is here to help you succeed. Schedule a free onboarding call or browse our help center.
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => window.location.href = '/help'}>
                  Visit Help Center
                </Button>
                <Button size="sm" className="bg-purple-600 hover:bg-purple-700">
                  Schedule Call
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
